import type { EventProps } from "react-big-calendar";
import { Star } from "lucide-react";

import type { CalendarEvent } from "@/components/calendar/types";
import { getCategoryColors } from "@/lib/outlookCategoryColors";
import { cn } from "@/lib/utils";

const PRIORITY_LABEL: Record<1 | 2 | 3, string> = {
  1: "bassa",
  2: "media",
  3: "alta",
};

/**
 * Custom event renderer for react-big-calendar.
 * Passed as `components={{ event: CalendarEventChip }}` and used by every view
 * (month / week / day).
 *
 * Background/border colour is applied by CalendarPage's `eventPropGetter`; this
 * component handles the chip content: title + a priority badge.
 *
 * The priority badge adapts to the category colour: on dark category
 * backgrounds it uses a light pill, on light backgrounds a dark pill, so the
 * amber stars always keep strong contrast regardless of the category.
 */
export function CalendarEventChip({ event }: EventProps<CalendarEvent>) {
  const { title, resource } = event;
  const priority = resource.priority as 0 | 1 | 2 | 3;

  const { foreground } = getCategoryColors(resource.category?.outlookColor);
  const onDarkBackground = foreground === "#ffffff";

  return (
    <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
      <span className="min-w-0 flex-1 truncate text-xs font-semibold leading-tight">
        {title}
      </span>
      {priority > 0 && (
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 shadow-sm ring-1",
            onDarkBackground
              ? "bg-white/95 ring-black/5"
              : "bg-slate-900/90 ring-white/10",
          )}
          aria-label={`Priorità ${PRIORITY_LABEL[priority as 1 | 2 | 3]}`}
          title={`Priorità ${PRIORITY_LABEL[priority as 1 | 2 | 3]}`}
        >
          {Array.from({ length: priority }, (_, i) => (
            <Star
              key={i}
              className="h-3 w-3 fill-amber-400 text-amber-400"
              strokeWidth={0}
            />
          ))}
        </span>
      )}
    </span>
  );
}
